/**
 * Everything the app needs from the outside world, built once at start-up
 * from the runtime config and handed down (CLAUDE.md → explicit dependency
 * injection): the backend, the connectivity manager (which endpoint the
 * backend is — PLAN §10.2), the session store, the sign-in actions and the
 * theme. `start.tsx` builds the real ones; nothing below the views reaches for
 * them — presentational components get props.
 */
import { type ApiClient, type ClientAnswer, createApiClient } from '@acp/api-client/client.js';
import { PROBE_TIMEOUT_MS, probeHealth } from '@acp/api-client/health.js';
import type { FetchPort } from '@acp/api-client/ports.js';
import type { ContractParsing } from '@acp/api-contract/parsing.js';
import { type Backend, createBackend, sessionStorageKey } from '../api/backend.js';
import { cloudPortalUrl, stageHosts, type StageHosts } from '../lib/hosts.js';
import { type Endpoint, statusEndpoints, watchedEndpoints } from '../connectivity/endpoints.js';
import { localNetworkPort, type PermissionsPort } from '../connectivity/localNetwork.js';
import { routeTarget } from '../connectivity/routing.js';
import { createFailoverApi, RESEND_WAIT_MS } from '../connectivity/failover.js';
import { type ConnectivityManager, createConnectivityManager } from '../connectivity/manager.js';
import { type ConnectivityState, endpointHasFeature } from '../connectivity/state.js';
import { clientHeaderValue } from '@acp/api-contract/clientIdentity.js';
import type { CognitoClientConfig, SrpLib } from '@acp/cognito-srp/srp.js';
import { cognitoLogin, type LoginOutcome, refreshCognitoSession } from '../auth/cognito.js';
import { devLogin, refreshDevSession } from '../auth/devLogin.js';
import { exchangeForLocalSession, localLogin, refreshLocalSession } from '../auth/localSession.js';
import type { LocalAuthStatusWire } from '@acp/api-contract/offlineAuth.js';
import { readLocalAuth } from '../api/localAuth.js';
import { createSessionStore, type Session, type SessionStore, type StoragePort } from '../auth/session.js';
import type { AppConfig } from '../lib/runtimeConfig.js';
import { createChangeLog, type ChangeLog } from '../lib/live/changeLog.js';
import { createDismissalStore, type DismissalStore } from '../lib/dismissals.js';
import { resolveEndpoints, type Shell } from '../lib/runtimeConfig.js';
import { applyTheme, initialTheme, THEME_STORAGE_KEY, type Theme } from '../lib/theme.js';

/** How this backend signs people in, as the login view needs it. */
export type AuthActions =
  | { readonly kind: 'dev'; readonly login: (userId: string) => Promise<void> }
  | { readonly kind: 'cognito'; readonly login: (email: string, password: string) => Promise<LoginOutcome> };

/**
 * A node's own sign-in (D20), present when the SPA is served by a node: what `GET /local/auth` says right now,
 * and break-glass sign-in (`POST /local/login`) — an offline PIN or the site's emergency account, accepted only
 * while the node cannot reach the cloud.
 */
export type OfflineActions = {
  readonly status: () => Promise<LocalAuthStatusWire>;
  readonly login: (account: string, secret: string) => Promise<void>;
};

export type ThemeStore = {
  readonly get: () => Theme;
  readonly set: (theme: Theme) => void;
  readonly subscribe: (listener: () => void) => () => void;
};

export type Services = {
  readonly config: AppConfig;
  readonly backend: Backend;
  /** The page's own backend with no token: the routes that take none (an invite link's set-password page, 7b). */
  readonly publicApi: ApiClient;
  readonly connectivity: ConnectivityManager;
  readonly sessions: SessionStore;
  readonly auth: AuthActions;
  /** Null unless the page is served by a node. */
  readonly offline: OfflineActions | null;
  readonly theme: ThemeStore;
  /** The wall clock (for "last answered 12 s ago"). */
  readonly nowMs: () => number;
  /** How answers and WS frames are read: tolerant in the app, strict in tests (D35, `@acp/api-contract/parsing.js`). */
  readonly responses: ContractParsing;
  /** The last change the tenant change channel announced per resource (D35): what an open editor learns who changed it from. */
  readonly liveChanges: ChangeLog;
  /** The announcements this browser dismissed (phase 7b's banner; a per-viewer convenience in storage). */
  readonly dismissals: DismissalStore;
  /** This browser's key/value storage, guarded (absent or throwing reads as empty): per-viewer conveniences only. */
  readonly storage: StoragePort;
  /** The cloud portal's origin (`lib/hosts.ts`' `cloudPortalUrl`): where a node-served page sends what only the cloud serves; null: none known. */
  readonly portalUrl: string | null;
  /** Which app this host serves (D39): the customer app, or the staff console (`config.json`'s `backend.kind: 'console'`). */
  readonly shell: Shell;
  /** The stage's two hosts (D39, `lib/hosts.ts`' `stageHosts`): where a staff user is sent from the app and a customer from the console; null: not known. */
  readonly hosts: StageHosts;
};

export type ServiceDeps = {
  readonly config: AppConfig;
  /** Which app this host serves (`ConfigResult.shell`); absent: the customer app. */
  readonly shell?: Shell;
  /** The page's origin, against which relative endpoints resolve. */
  readonly origin: string;
  readonly fetch: FetchPort;
  readonly storage: StoragePort;
  readonly nowMs: () => number;
  /** Run `fn` after `ms`; returns a cancel function (the connectivity probes' schedule). */
  readonly after: (ms: number, fn: () => void) => () => void;
  /** The SRP library for a Cognito pool (injected: `realSrpLib` in the app, a fake in tests). */
  readonly srpLib: (config: CognitoClientConfig) => SrpLib;
  /** The document root the theme class goes on. */
  readonly root: { readonly classList: { toggle: (name: string, force: boolean) => unknown } };
  /**
   * How answers and WS frames are read (D35): `'tolerant'` in the app — a page built (or cached by the service worker)
   * before an additive API change keeps working against the newer server — `'strict'` in tests.
   */
  readonly responses: ContractParsing;
  /** `navigator.permissions`, for Chrome's Local Network Access (`connectivity/localNetwork.ts`); null where there is none (tests). */
  readonly permissions: PermissionsPort | null;
  /**
   * This build's commit and commit time as the CloudFront build embeds them (`main.tsx`; null: a build that names none —
   * the release build, which must not change while its source does not, D40), for its `x-acp-client` (D37 §4.1):
   * `web/…` on a cloud page, `web-node/<the serving node's release commit>` on a page a node serves (from that node's
   * `/health.release`) — a node held on a pin serves an old build by design, which the cloud's floor must not refuse.
   * Sent only to an endpoint whose `/health` lists `client-header` (CORS). Absent: no header at all.
   */
  readonly clientBuild?: { readonly commit: string; readonly builtAtMs: number | null } | null;
  /** A host answered `426 upgrade-required`: this build is older than it serves (the reload prompt, `main.tsx`). */
  readonly onUpgradeRequired?: (message: string) => void;
};

export function createThemeStore(storage: StoragePort, root: ServiceDeps['root']): ThemeStore {
  let theme = initialTheme(storage.get(THEME_STORAGE_KEY));
  applyTheme(root, theme);
  const listeners = new Set<() => void>();
  return {
    get: () => theme,
    set: (next) => {
      theme = next;
      storage.set(THEME_STORAGE_KEY, next);
      applyTheme(root, next);
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function createServices(deps: ServiceDeps): Services {
  const { config } = deps;
  const auth = config.auth;
  const { apiUrl } = resolveEndpoints(config.backend, deps.origin);
  // The node the SPA talks to now (after a failover, the peer — which accepts the same local session, D20).
  let nodeApiUrl = (): string => apiUrl;
  const refreshLocal = refreshLocalSession(deps.fetch, () => nodeApiUrl(), deps.nowMs);
  const refreshUpstream = auth.kind === 'cognito' ? (s: Session) => refreshCognitoSession(deps.fetch, auth, s) : refreshDevSession;
  const refresh = (s: Session): Promise<Session | null> => (s.provider === 'local' ? refreshLocal(s) : refreshUpstream(s));
  const servedByNode = config.backend.kind === 'node';
  // On a node's page, the Cognito/dev session gets a local session held beside it (D20): node calls send that,
  // cloud calls (the connectivity panel's GET /status) keep the Cognito token.
  const exchangeLocal = servedByNode
    ? async (token: string) => {
        const local = await exchangeForLocalSession(deps.fetch, nodeApiUrl(), token);
        return local === null ? null : { idToken: local.idToken, expiresAtMs: local.expiresAtMs };
      }
    : null;
  const sessions = createSessionStore({ storage: deps.storage, storageKey: sessionStorageKey(config, apiUrl), refresh, refreshLocal, exchangeLocal, nowMs: deps.nowMs });
  const clients = new Map<string, ApiClient>();
  // D37: who this is (only to a host that allows the header), and the reload prompt when a host says it is too old.
  let connectivityState: (() => ConnectivityState) | null = null;
  const clientBuild = deps.clientBuild;
  const identity = (endpointId: string) => (): string | null => {
    if (clientBuild === undefined || connectivityState === null) return null;
    const state = connectivityState();
    const header = clientHeaderFor(clientBuild, servedByNode ? (state.endpoints.find((s) => s.endpoint.kind === 'node' && s.endpoint.apiUrl === apiUrl)?.releaseCommit ?? null) : null, servedByNode);
    return endpointHasFeature(state, endpointId, 'client-header') ? header : null;
  };
  // A node's page is never prompted: its build is its node's release, which a reload cannot change.
  const onAnswer = upgradeWatcher(servedByNode ? null : (deps.onUpgradeRequired ?? null));
  const clientFor = (endpoint: Endpoint): ApiClient => {
    const existing = clients.get(endpoint.id);
    if (existing !== undefined) return existing;
    // A node is sent the local session (when one is held), the cloud the Cognito token (`chooseToken`).
    const client = createApiClient({ baseUrl: endpoint.apiUrl, fetch: deps.fetch, token: endpoint.kind === 'node' ? sessions.nodeToken : sessions.idToken, responses: deps.responses, client: identity(endpoint.id), onAnswer });
    clients.set(endpoint.id, client);
    return client;
  };
  const statusClient = (cloud: Endpoint): ApiClient => createApiClient({ baseUrl: cloud.apiUrl, fetch: deps.fetch, token: () => sessions.idToken(false), responses: deps.responses, client: identity(cloud.id), onAnswer });
  const connectivity = createConnectivityManager({
    endpoints: watchedEndpoints(config, deps.origin),
    pageKind: config.backend.kind,
    probe: (endpoint) => probeHealth({ fetch: deps.fetch, nowMs: deps.nowMs, after: deps.after }, endpoint.apiUrl, PROBE_TIMEOUT_MS),
    // The cloud's view needs a token the cloud accepts — the same Cognito session (§11). Its token is refreshed near
    // expiry like any request's, but a 401 never FORCES a refresh: the cloud refusing a token it cannot verify (a
    // node's dev token) must not sign the operator out.
    readCloudView: async (cloud) => {
      if (sessions.getSession() === null) return null;
      return statusClient(cloud).call('getStatus', { params: null, query: null, body: null });
    },
    // A cloud page learns the tenant's nodes from the cloud's view (its `/config.json` is every tenant's).
    learnEndpoints: (view, known) => statusEndpoints(view, config, deps.origin, known),
    after: deps.after,
    nowMs: deps.nowMs,
    localNetwork: localNetworkPort(deps.permissions, config.backend.kind),
  });
  connectivityState = connectivity.getState;
  const backend = createBackend(connectivity.primary, createFailoverApi({ manager: connectivity, clientFor, resendWaitMs: RESEND_WAIT_MS, target: (route) => routeTarget(route, connectivity.getState(), sessions.getSession()?.provider ?? null) }));
  nodeApiUrl = () => backend.apiUrl;
  /** Sign in; on a node's page, also get the local session to hold beside it (D20) — without it, the own token serves. */
  const signInHere = async (session: Session): Promise<void> => {
    sessions.signIn(session);
    if (servedByNode) await sessions.nodeToken(false).catch(() => undefined);
  };
  const actions: AuthActions =
    auth.kind === 'cognito'
      ? {
          kind: 'cognito',
          login: async (email, password) => {
            const outcome = await cognitoLogin(deps.srpLib(auth), email, password);
            return signingIn(outcome, signInHere);
          },
        }
      : {
          kind: 'dev',
          login: async (userId) => {
            await signInHere(await devLogin(deps.fetch, apiUrl, userId));
          },
        };
  const offline: OfflineActions | null = servedByNode
    ? {
        status: async () => readLocalAuth(deps.fetch, nodeApiUrl()),
        login: async (account, secret) => {
          sessions.signIn(await localLogin(deps.fetch, nodeApiUrl(), account, secret));
        },
      }
    : null;
  const publicApi = createApiClient({ baseUrl: apiUrl, fetch: deps.fetch, token: null, responses: deps.responses });
  return { config, backend, publicApi, connectivity, sessions, auth: actions, offline, theme: createThemeStore(deps.storage, deps.root), nowMs: deps.nowMs, responses: deps.responses, liveChanges: createChangeLog(), dismissals: createDismissalStore(deps.storage), storage: deps.storage, portalUrl: cloudPortalUrl(config, deps.origin), shell: deps.shell ?? 'app', hosts: stageHosts(apiUrl, deps.shell ?? 'app', deps.origin) };
}

/**
 * This page's `x-acp-client` (D37 §4.1): a cloud page names its embedded build (`web/unknown` when it has none: its age
 * is then never judged); a node's page names the release its node runs (`web-node/<commit>`, from `/health.release`),
 * else the embedded build, else `unknown` — `web-node` is never refused by the floor.
 */
export function clientHeaderFor(build: { readonly commit: string; readonly builtAtMs: number | null } | null, nodeReleaseCommit: string | null, servedByNode: boolean): string {
  if (!servedByNode) return clientHeaderValue('web', build?.commit ?? 'unknown', build?.builtAtMs ?? null);
  return clientHeaderValue('web-node', nodeReleaseCommit ?? build?.commit ?? 'unknown', null);
}

/**
 * The `onAnswer` hook that raises the reload prompt (D37 §4.1): the first `426 upgrade-required` a host answers, once
 * — every request after it would say the same, and one prompt is enough. Null: no prompt wired (tests).
 */
export function upgradeWatcher(onUpgradeRequired: ((message: string) => void) | null): (answer: ClientAnswer) => void {
  let raised = false;
  return (answer) => {
    if (answer.status !== 426 || raised || onUpgradeRequired === null) return;
    raised = true;
    onUpgradeRequired('This version of ACP is older than the server supports. Reload to get the current one.');
  };
}

/** Store the session when an outcome (or a completed new-password challenge) signs in. */
async function signingIn(outcome: LoginOutcome, signIn: (session: Session) => Promise<void>): Promise<LoginOutcome> {
  if (outcome.type === 'signed-in') {
    await signIn(outcome.session);
    return outcome;
  }
  return { type: 'new-password-required', complete: async (newPassword) => signingIn(await outcome.complete(newPassword), signIn) };
}
